/**
 * Shared axios instance with a request interceptor that injects the Keycloak
 * bearer token on every call.
 *
 * Session 5 (MULTI-TENANT-MIGRATION-PLAN.md Lesson 4): all backend routers now
 * require `Authorization: Bearer <token>`. Manually adding `headers` to every
 * axios call across the 6 pages is ~100+ lines of plumbing and an easy place
 * to forget one (the dress rehearsal lost the Dashboard this way). Instead the
 * Keycloak object is registered with the interceptor once at app boot, and
 * every axios call below auto-attaches the token.
 *
 * Usage:
 *   import api from '../utils/axiosInstance';
 *   await api.get(`${API_BASE_URL}/tasks/practice/${practiceId}`);
 */
import axios from 'axios';

// Module-level keycloak ref. Updated by `setKeycloak` from App.js after
// Keycloak finishes initializing. The interceptor reads `keycloak.token` at
// request time, so token refreshes are picked up automatically.
let keycloakRef = null;

export const setKeycloak = (keycloak) => {
  keycloakRef = keycloak;
};

// Identifies THIS browser tab for the whole session. Sent on every request as
// `X-Client-Instance`; the backend echoes it back as the `origin` of the
// resulting `config_changed` event, so the tab that made a change can ignore its
// own event instead of reloading over the form it is already showing.
// Deliberately per-tab, not per-user: two tabs of the same user must still sync.
// `crypto.randomUUID` needs a secure context (https or localhost) — both dev and
// prod qualify, but keep the fallback so a plain-http LAN test doesn't crash.
export const TAB_ID = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Returns the current Keycloak bearer token (or null). For out-of-band requests
// that cannot go through the axios interceptor — e.g. a keepalive `fetch` fired
// on window `pagehide`, where the page is being torn down.
export const getAuthToken = () => keycloakRef?.token || null;

const api = axios.create();

api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};
    config.headers['X-Client-Instance'] = TAB_ID;

    const kc = keycloakRef;
    if (kc?.token) {
      // Keep the token fresh so a request fired right after the tab was idle or
      // slept doesn't go out with an expired token (which would 401 and, for the
      // silent fallback poll, fail invisibly). updateToken is a no-op that
      // resolves immediately WITHOUT a network call while the token still has
      // >30s of validity, and only refreshes when near expiry. A failure here is
      // swallowed on purpose — the request proceeds and any resulting 401 is
      // handled upstream; the re-login decision lives in App.js (debounced), so
      // a background poll never triggers a redirect.
      if (typeof kc.updateToken === 'function') {
        try { await kc.updateToken(30); } catch { /* proceed; upstream handles auth */ }
      }
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${kc.token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default api;
